import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileCheck, Download, CheckCircle, Banknote, Building, CreditCard, Euro, FileText, Landmark, PieChart, Receipt, Repeat } from 'lucide-react';
import { formatDate, getPaymentMethodConfig } from './constants';

export default function ArchivesTab({
  dashboardStats, calculateMontantTTC, downloadPartialReceipt: downloadPartialReceipt, downloadRecu: downloadRecu, setActiveTab
}) {
  return (
        <div className="space-y-6" data-testid="archives-content">
          {/* En-tête Premium */}
          <div className="bg-gradient-to-r from-slate-800 via-gray-800 to-zinc-900 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                  <Building className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Gestion Électronique des Documents</h3>
                  <p className="text-slate-300 mt-1">
                    Reçus de paiement et documents archivés de manière sécurisée
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-4xl font-black">{dashboardStats.totalArchives || 0}</p>
                <p className="text-slate-400 text-sm">documents archivés</p>
              </div>
            </div>
          </div>

          {/* Statistiques archives - Design Cards Premium */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Card className="border-0 shadow-lg bg-white overflow-hidden group hover:shadow-xl transition-all duration-300">
              <CardContent className="p-0">
                <div className="h-1.5 bg-gradient-to-r from-slate-500 to-gray-600" />
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-slate-100 rounded-xl group-hover:bg-slate-200 transition-colors">
                        <Receipt className="h-6 w-6 text-slate-700" />
                      </div>
                      <div>
                        <p className="text-3xl font-black text-gray-900">{dashboardStats.paiementsArchives?.length || 0}</p>
                        <p className="text-sm text-gray-500 font-medium">Reçus de paiement</p>
                      </div>
                    </div>
                    <Badge className="bg-slate-600 text-white font-semibold">PDF</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg bg-white overflow-hidden group hover:shadow-xl transition-all duration-300">
              <CardContent className="p-0">
                <div className="h-1.5 bg-gradient-to-r from-indigo-500 to-blue-600" />
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-indigo-100 rounded-xl group-hover:bg-indigo-200 transition-colors">
                        <FileCheck className="h-6 w-6 text-indigo-700" />
                      </div>
                      <div>
                        <p className="text-3xl font-black text-gray-900">{dashboardStats.documentsArchives?.length || 0}</p>
                        <p className="text-sm text-gray-500 font-medium">Dossiers archivés</p>
                      </div>
                    </div>
                    <Badge className="bg-indigo-600 text-white font-semibold">Signés</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg bg-white overflow-hidden group hover:shadow-xl transition-all duration-300">
              <CardContent className="p-0">
                <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-teal-500" />
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-emerald-100 rounded-xl group-hover:bg-emerald-200 transition-colors">
                        <Euro className="h-6 w-6 text-emerald-700" />
                      </div>
                      <div>
                        <p className="text-3xl font-black text-gray-900">
                          {(dashboardStats.paiementsArchives?.reduce((sum, p) => sum + (p.amount || p.montant || 0), 0) || 0).toLocaleString('fr-FR')} €
                        </p>
                        <p className="text-sm text-gray-500 font-medium">Montant archivé</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-600 text-white font-semibold">Total</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Liste des reçus archivés */}
          {dashboardStats.paiementsArchives?.length === 0 && dashboardStats.documentsArchives?.length === 0 ? (
            <Card className="border-2 border-dashed border-gray-200 bg-gray-50/50">
              <CardContent className="py-16 text-center">
                <div className="p-4 bg-gray-100 rounded-full w-fit mx-auto mb-4">
                  <Building className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-700">Aucun document archivé</h3>
                <p className="text-gray-500 mt-3 max-w-md mx-auto">
                  Les reçus de paiement et documents archivés dans la GED apparaîtront ici.
                </p>
                <Button variant="outline" className="mt-6" onClick={() => setActiveTab('partiels')}>
                  <PieChart className="h-4 w-4 mr-2" />
                  Aller aux paiements partiels
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-5">
              {/* Reçus de paiement archivés */}
              {dashboardStats.paiementsArchives?.length > 0 && (
                <Card className="border-0 shadow-lg overflow-hidden">
                  <CardHeader className="pb-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b">
                    <CardTitle className="text-lg flex items-center gap-3">
                      <div className="p-2 bg-slate-600 rounded-lg">
                        <Receipt className="h-5 w-5 text-white" />
                      </div>
                      Reçus de Paiement Archivés
                      <span className="ml-auto px-3 py-1 bg-slate-600 text-white rounded-full text-sm font-bold">
                        {dashboardStats.paiementsArchives.length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-gray-100">
                      {dashboardStats.paiementsArchives.map((paiement, idx) => {
                        const methodConfig = getPaymentMethodConfig(paiement.method || paiement.mode_paiement);
                        return (
                          <div key={idx} className="p-5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="relative">
                                <div className="p-3 bg-slate-100 rounded-xl">
                                  <Receipt className="h-6 w-6 text-slate-600" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 p-1 bg-emerald-500 rounded-full">
                                  <CheckCircle className="h-3 w-3 text-white" />
                                </div>
                              </div>
                              <div>
                                <p className="font-bold text-gray-900">{paiement.raison_sociale}</p>
                                <p className="text-sm text-gray-500">{paiement.numero_police}</p>
                                {paiement.numero_recu && (
                                  <p className="text-xs text-slate-400 font-mono mt-1">Reçu #{paiement.numero_recu}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-5">
                              <div className="text-right">
                                <p className="font-black text-xl text-gray-900">{(paiement.amount || paiement.montant || 0).toLocaleString('fr-FR')} €</p>
                                <div className="flex items-center gap-2 justify-end mt-1">
                                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border ${methodConfig.color}`}>
                                    {methodConfig.icon === 'Landmark' && <Landmark className="h-3 w-3" />}
                                    {methodConfig.icon === 'Repeat' && <Repeat className="h-3 w-3" />}
                                    {methodConfig.icon === 'CreditCard' && <CreditCard className="h-3 w-3" />}
                                    {methodConfig.icon === 'FileText' && <FileText className="h-3 w-3" />}
                                    {methodConfig.icon === 'Banknote' && <Banknote className="h-3 w-3" />}
                                    {methodConfig.shortLabel}
                                  </span>
                                  <span className="text-xs text-gray-500">{formatDate(paiement.date)}</span>
                                </div>
                              </div>
                              <Badge className="bg-emerald-700 text-white flex items-center gap-1.5 font-bold px-3 py-1.5">
                                <CheckCircle className="h-4 w-4" />
                                GED
                              </Badge>
                              {paiement.recu_path && (
                                <Button 
                                  size="sm" 
                                  className="bg-slate-600 hover:bg-slate-700 text-white"
                                  onClick={() => downloadPartialReceipt(paiement.souscription_id, paiement.recu_path)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Documents contrats archivés */}
              {dashboardStats.documentsArchives?.length > 0 && (
                <Card className="border-0 shadow-lg overflow-hidden">
                  <CardHeader className="pb-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b">
                    <CardTitle className="text-lg flex items-center gap-3">
                      <div className="p-2 bg-indigo-600 rounded-lg">
                        <FileCheck className="h-5 w-5 text-white" />
                      </div>
                      Dossiers Archivés
                      <span className="ml-auto px-3 py-1 bg-indigo-600 text-white rounded-full text-sm font-bold">
                        {dashboardStats.documentsArchives.length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-gray-100">
                      {dashboardStats.documentsArchives.map((doc, idx) => (
                        <div key={idx} className="py-3 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-indigo-100 rounded-lg">
                              <FileCheck className="h-5 w-5 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-semibold">{doc.raison_sociale}</p>
                              <p className="text-sm text-gray-500">{doc.numero_police || doc.id}</p>
                              {doc.siret && <p className="text-xs text-gray-400">SIRET: {doc.siret}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold text-lg">{calculateMontantTTC(doc).toLocaleString('fr-FR')} €</p>
                              <p className="text-xs text-gray-500">Archivé le {formatDate(doc.archive_date)}</p>
                            </div>
                            <Badge className="bg-green-600 text-white flex items-center gap-1 font-semibold">
                              <CheckCircle className="h-3 w-3" />
                              Soldé
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
  );

}
